import { fmtCurrency } from '../../../utils/formatNumber';
import styles from '../Reports.module.css';

const SubGroupSection = ({ subGroups }) => (
  <>
    {subGroups?.map((sg) => (
      <tbody key={sg.name}>
        <tr style={{ background: 'var(--color-surface-raised)' }}>
          <td colSpan={2} style={{ fontWeight: 600, paddingLeft: 8 }}>{sg.name}</td>
        </tr>
        {sg.rows.map((r, i) => (
          <tr key={i}>
            <td style={{ paddingLeft: 20 }}>{r.name}</td>
            <td className={styles.amtRight}>{fmtCurrency(r.balance)}</td>
          </tr>
        ))}
        <tr style={{ borderTop: '1px solid var(--color-border)' }}>
          <td style={{ paddingLeft: 8, color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
            Subtotal - {sg.name}
          </td>
          <td className={styles.amtRight} style={{ color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
            {fmtCurrency(sg.subtotal)}
          </td>
        </tr>
      </tbody>
    ))}
  </>
);

const FlatSection = ({ rows, emptyLabel }) => (
  <tbody>
    {rows?.length
      ? rows.map((row, index) => (
          <tr key={`${row.accountCode || row.name}-${index}`}>
            <td>{row.name}</td>
            <td className={styles.amtRight}>{fmtCurrency(row.balance ?? 0)}</td>
          </tr>
        ))
      : (
        <tr>
          <td colSpan={2} style={{ color: 'var(--color-text-secondary)' }}>{emptyLabel}</td>
        </tr>
      )}
  </tbody>
);

export default function BalanceSheetReport({ data }) {
  if (!data) return null;

  const { assets, liabilities, equity, incomeExpense, totalLiabilitiesEquity, isBalanced } = data;
  const surplus = incomeExpense?.surplus ?? 0;

  return (
    <div>
      <div className={styles.twoColGrid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><h3>Liabilities</h3></div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className={styles.amtRight}>Amount (Rs)</th>
                  </tr>
                </thead>
                <SubGroupSection subGroups={liabilities?.subGroups} />
                {!liabilities?.subGroups?.length && (
                  <tbody>
                    <tr>
                      <td colSpan={2} style={{ color: 'var(--color-text-secondary)' }}>No liability accounts</td>
                    </tr>
                  </tbody>
                )}
                <tfoot>
                  <tr>
                    <td><strong>Total Liabilities</strong></td>
                    <td className={styles.amtRight}><strong>{fmtCurrency(liabilities?.total)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Equity &amp; Corpus</h3></div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className={styles.amtRight}>Amount (Rs)</th>
                  </tr>
                </thead>
                <FlatSection rows={equity?.rows} emptyLabel="No equity accounts" />
                <tbody>
                  <tr>
                    <td><em>Net Income for Period</em></td>
                    <td className={`${styles.amtRight} ${surplus >= 0 ? styles.balanceOk : styles.balanceDiff}`}>
                      {fmtCurrency(surplus)}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total Equity</strong></td>
                    <td className={styles.amtRight}>
                      <strong>{fmtCurrency((equity?.total ?? 0) + surplus)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Assets</h3></div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.amtRight}>Amount (Rs)</th>
                </tr>
              </thead>
              <SubGroupSection subGroups={assets?.subGroups} />
              <tfoot>
                <tr>
                  <td><strong>Total Assets</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(assets?.total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ flex: 1, padding: '16px 24px', textAlign: 'center', borderRight: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              Total Liabilities + Equity
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>
              {fmtCurrency(totalLiabilitiesEquity ?? 0)}
            </div>
          </div>

          <div style={{ flex: 1, padding: '16px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              Total Assets
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>
              {fmtCurrency(assets?.total)}
            </div>
          </div>
        </div>

        <div
          className={isBalanced ? styles.balanceOk : styles.balanceDiff}
          style={{ padding: '10px 24px', textAlign: 'center', fontWeight: 700, fontSize: '1rem' }}
        >
          {isBalanced
            ? 'Liabilities + Equity = Assets'
            : `Difference: ${fmtCurrency(Math.abs((assets?.total ?? 0) - (totalLiabilitiesEquity ?? 0)))}`}
        </div>
      </div>
    </div>
  );
}
